/**
 * Contact-secondary / ticket scrollspy topic filters.
 * Loaded only from contact-secondary section; does not modify core scrollspy behavior.
 */
(function () {
  if (window.ContentScrollSpyContactFilters) return;

  const SELECTORS = {
    SCROLLSPY: 'content-scrollspy',
    CONTACT_ROOT: '.contact-secondary-wrap',
    FILTERS: '[data-scrollspy-contact-filters]',
    TOPIC_SELECT: '[data-scrollspy-contact-topic-select]',
    SUBTOPIC_SELECT: '[data-scrollspy-contact-subtopic-select]'
  };

  function normalizeLabel(value) {
    return (value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  class ContentScrollSpyContactFilters {
    constructor(scrollspy) {
      this.scrollspy = scrollspy;
      this.topicNodes = [];
      this.topicSelect = null;
      this.subtopicSelect = null;
      this.filters = null;
    }

    static isContactContext() {
      return !!document.querySelector(SELECTORS.CONTACT_ROOT);
    }

    init() {
      if (!ContentScrollSpyContactFilters.isContactContext()) return;

      const sections = this.scrollspy.getSections();
      if (!sections.length) return;

      this.topicNodes = this.buildTopicNodes(sections);
      if (!this.topicNodes.some((node) => node.children.length > 0)) return;

      this.mountFilters();
      this.bindFilterEvents();
      this.applyPreselectedParams();
    }

    destroy() {
      if (this.filters) {
        this.filters.remove();
        this.filters = null;
      }
      this.topicSelect = null;
      this.subtopicSelect = null;
      this.topicNodes = [];
    }

    mountFilters() {
      const existing = this.scrollspy.querySelector(SELECTORS.FILTERS);
      if (existing) {
        this.filters = existing;
      } else {
        this.filters = document.createElement('div');
        this.filters.className = 'content-scrollspy__filters content-scrollspy__filters--contact';
        this.filters.setAttribute('data-scrollspy-contact-filters', '');
        this.filters.innerHTML = `
          <label class="content-scrollspy__filter">
            <span class="content-scrollspy__filter-label">Topic</span>
            <select class="content-scrollspy__filter-select" data-scrollspy-contact-topic-select>
              <option value="">Select topic</option>
            </select>
          </label>
          <label class="content-scrollspy__filter">
            <span class="content-scrollspy__filter-label">Subtopic</span>
            <select class="content-scrollspy__filter-select" data-scrollspy-contact-subtopic-select>
              <option value="">Select subtopic</option>
            </select>
          </label>
        `;

        const menu = this.scrollspy.querySelector('[data-scrollspy-menu]');
        if (menu) {
          this.scrollspy.insertBefore(this.filters, menu);
        } else {
          this.scrollspy.appendChild(this.filters);
        }
      }

      this.topicSelect = this.filters.querySelector(SELECTORS.TOPIC_SELECT);
      this.subtopicSelect = this.filters.querySelector(SELECTORS.SUBTOPIC_SELECT);
      if (!this.topicSelect || !this.subtopicSelect) return;

      this.topicSelect.innerHTML = '<option value="">Select topic</option>';
      this.topicNodes.forEach((topic) => {
        const option = document.createElement('option');
        option.value = String(topic.index);
        option.textContent = topic.label || `Topic ${topic.index + 1}`;
        this.topicSelect.appendChild(option);
      });
    }

    bindFilterEvents() {
      if (!this.topicSelect || !this.subtopicSelect) return;

      this.topicSelect.onchange = () => {
        const index = Number.parseInt(this.topicSelect.value, 10);
        if (!Number.isFinite(index)) {
          this.hydrateSubtopicOptions(null);
          return;
        }
        this.hydrateSubtopicOptions(index);
        this.scrollspy.goToSection(index);
      };

      this.subtopicSelect.onchange = () => {
        const index = Number.parseInt(this.subtopicSelect.value, 10);
        if (Number.isFinite(index)) {
          this.scrollspy.goToSection(index);
        }
      };

      this.hydrateSubtopicOptions(this.topicNodes[0].index);
    }

    hydrateSubtopicOptions(topicIndex) {
      if (!this.subtopicSelect) return;
      const topic = this.topicNodes.find((item) => item.index === topicIndex);
      this.subtopicSelect.innerHTML = '<option value="">All subtopics</option>';
      if (!topic || !topic.children.length) {
        this.subtopicSelect.disabled = true;
        return;
      }
      this.subtopicSelect.disabled = false;
      topic.children.forEach((child) => {
        const option = document.createElement('option');
        option.value = String(child.index);
        option.textContent = child.label || `Subtopic ${child.index + 1}`;
        this.subtopicSelect.appendChild(option);
      });
    }

    applyPreselectedParams() {
      const preselected = this.getTopicSelectionFromParams();
      if (!preselected || !this.topicSelect || !this.subtopicSelect) return;

      this.topicSelect.value = String(preselected.topicIndex);
      this.hydrateSubtopicOptions(preselected.topicIndex);
      if (preselected.subtopicIndex !== undefined && preselected.subtopicIndex !== null) {
        this.subtopicSelect.value = String(preselected.subtopicIndex);
      }
      this.scrollspy.goToSection(preselected.subtopicIndex ?? preselected.topicIndex);
    }

    getTopicSelectionFromParams() {
      const params = new URLSearchParams(window.location.search);
      const category1 = normalizeLabel(params.get('category1') || params.get('category'));
      const category2 = normalizeLabel(params.get('category2') || params.get('subcategory'));
      if (!category1) return null;

      const topic = this.topicNodes.find((item) => normalizeLabel(item.label) === category1);
      if (!topic) return null;

      if (!category2) {
        return { topicIndex: topic.index, subtopicIndex: null };
      }
      const child = topic.children.find((item) => normalizeLabel(item.label) === category2);
      return { topicIndex: topic.index, subtopicIndex: child ? child.index : null };
    }

    syncTopicSelection(index) {
      if (!this.topicSelect || !this.subtopicSelect || !this.topicNodes.length) return;
      const topic = this.topicNodes.find(
        (item) => item.index === index || item.children.some((child) => child.index === index)
      );
      if (!topic) return;

      this.topicSelect.value = String(topic.index);
      this.hydrateSubtopicOptions(topic.index);
      const subtopic = topic.children.find((child) => child.index === index);
      this.subtopicSelect.value = subtopic ? String(subtopic.index) : '';
    }

    buildTopicNodes(sections) {
      const contactNodes = this.buildTopicNodesFromContactSecondary(sections);
      if (contactNodes.length) return contactNodes;
      return [];
    }

    buildTopicNodesFromContactSecondary(sections) {
      const contactRoot = document.querySelector(SELECTORS.CONTACT_ROOT);
      if (!contactRoot) return [];

      const topicLabel =
        (document.getElementById('page-title') && document.getElementById('page-title').textContent) ||
        (document.getElementById('current-category') && document.getElementById('current-category').textContent) ||
        '';

      const subcategoryHeadings = Array.from(
        contactRoot.querySelectorAll('.faq-content-container .subcategory-title')
      );
      if (!subcategoryHeadings.length) return [];

      const headingIndexByElement = new Map();
      sections.forEach((heading, index) => {
        headingIndexByElement.set(heading, index);
      });

      const children = subcategoryHeadings
        .map((el) => ({
          index: headingIndexByElement.get(el),
          label: (el.textContent || '').trim()
        }))
        .filter((item) => Number.isFinite(item.index));

      if (!children.length) return [];

      return [
        {
          index: children[0].index,
          label: (topicLabel || '').trim() || 'Topic',
          children
        }
      ];
    }
  }

  let activeInstance = null;

  function handleScrollspyReady(event) {
    if (!ContentScrollSpyContactFilters.isContactContext()) return;
    const scrollspy = event.detail && event.detail.scrollspy;
    if (!scrollspy) return;

    if (activeInstance) {
      activeInstance.destroy();
      activeInstance = null;
    }

    activeInstance = new ContentScrollSpyContactFilters(scrollspy);
    activeInstance.init();
  }

  function handleSectionChanged(event) {
    if (!activeInstance) return;
    const sectionIndex = event.detail && event.detail.sectionIndex;
    if (sectionIndex === undefined || sectionIndex === null) return;
    activeInstance.syncTopicSelection(sectionIndex);
  }

  document.addEventListener('scrollspy:ready', handleScrollspyReady);
  document.addEventListener('scrollspy:section-changed', handleSectionChanged);

  window.ContentScrollSpyContactFilters = ContentScrollSpyContactFilters;
})();
